import React, { useEffect, useState } from 'react';
import { Toaster, toast } from 'sonner';
import { Plus, PencilSimple, Trash, Buildings, ArrowCounterClockwise, Warning } from '@phosphor-icons/react';
import { useI18n } from '../../contexts/I18nContext';
import { useAuth } from '../../contexts/AuthContext';
import { useOrg } from '../../contexts/OrgContext';
import {
  adminListBuildings, adminCreateBuilding, adminUpdateBuilding, adminDeleteBuilding,
  adminRestoreBuilding, adminHardDeleteBuilding,
  adminListOrgs, formatAdminError,
} from '../../lib/admin';
import AdminPage from '../../components/admin/AdminPage';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import ArchiveToggle from '../../components/admin/ArchiveToggle';
import { Field, inputCls, selectCls, Modal } from '../../components/admin/Field';

export default function AdminBuildings() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { current, isSuperAdmin } = useOrg();
  const [items, setItems] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [hardConfirm, setHardConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [viewArchived, setViewArchived] = useState(false);

  const allowed = user?.role === 'admin';

  const reload = async () => {
    setLoading(true);
    try {
      const { items } = await adminListBuildings({ archived: viewArchived });
      // When viewing archived: backend returns ALL rows with include_deleted=true,
      // so we filter down to just the soft-deleted ones client-side.
      const filtered = viewArchived ? items.filter((b) => b.deleted_at) : items;
      setItems(filtered);
    } catch (err) {
      toast.error(formatAdminError('List buildings', err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!allowed) return;
    reload();
    if (isSuperAdmin) adminListOrgs().then(({ items }) => setOrgs(items)).catch(() => setOrgs([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, isSuperAdmin, viewArchived]);

  const onSave = async (form) => {
    setBusy(true);
    try {
      if (editing === 'new') {
        await adminCreateBuilding(form);
        toast.success(t('admin_building_created'));
      } else {
        await adminUpdateBuilding(editing.id, form);
        toast.success(t('admin_building_updated'));
      }
      setEditing(null);
      reload();
    } catch (err) {
      toast.error(formatAdminError(editing === 'new' ? 'Create building' : 'Update building', err));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setBusy(true);
    try {
      await adminDeleteBuilding(confirm.id);
      toast.success(t('admin_building_deleted'));
      setConfirm(null);
      reload();
    } catch (err) {
      toast.error(formatAdminError('Delete building', err));
    } finally {
      setBusy(false);
    }
  };

  const onRestore = async (b) => {
    setBusy(true);
    try {
      await adminRestoreBuilding(b.id);
      toast.success(t('admin_restored'));
      reload();
    } catch (err) {
      toast.error(formatAdminError('Restore building', err));
    } finally {
      setBusy(false);
    }
  };

  const onHardDelete = async () => {
    setBusy(true);
    try {
      await adminHardDeleteBuilding(hardConfirm.id);
      toast.success(t('admin_hard_deleted'));
      setHardConfirm(null);
      reload();
    } catch (err) {
      toast.error(formatAdminError('Permanently delete building', err));
    } finally {
      setBusy(false);
    }
  };

  if (!allowed) {
    return (
      <AdminPage title={t('admin_buildings_title')}>
        <Toaster position="top-right" richColors />
        <div className="bg-bg-alt rounded-xl p-8 text-center text-ink-soft">{t('admin_admin_only')}</div>
      </AdminPage>
    );
  }

  return (
    <AdminPage
      title={t('admin_buildings_title')}
      description={isSuperAdmin ? t('admin_buildings_sub_super') : t('admin_buildings_sub_org', { org: current?.name || '—' })}
    >
      <Toaster position="top-right" richColors />

      <div className="flex items-center justify-end mb-3 flex-wrap gap-3">
        {!viewArchived && (
          <button type="button" onClick={() => setEditing('new')} data-testid="admin-building-new"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sage text-white text-sm font-medium hover:bg-sage-700">
            <Plus size={16} weight="bold" /> {t('admin_building_new')}
          </button>
        )}
      </div>

      {isSuperAdmin && (
        <div className="flex items-center mb-3">
          <ArchiveToggle value={viewArchived} onChange={setViewArchived} />
        </div>
      )}

      <div className="bg-white border border-stone_line rounded-2xl shadow-soft overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-ink-muted">{t('loading')}</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-ink-muted">
            {viewArchived ? t('admin_no_archived') : t('no_results')}
          </div>
        ) : (
          <ul className="divide-y divide-stone_line" data-testid="admin-buildings-list">
            {items.map((b) => (
              <li key={b.id} className={`px-5 py-4 flex items-center gap-4 ${viewArchived ? 'opacity-70' : ''}`}>
                <div className="w-9 h-9 rounded-lg bg-sage-100 text-sage-600 grid place-items-center">
                  <Buildings size={18} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-ink truncate">{b.name}</div>
                  {b.description && <div className="text-xs text-ink-muted truncate">{b.description}</div>}
                </div>
                <div className="flex items-center gap-1">
                  {viewArchived ? (
                    <>
                      <button onClick={() => onRestore(b)} disabled={busy}
                        data-testid={`admin-building-restore-${b.id}`}
                        className="p-2 rounded-lg text-ink-soft hover:bg-sage-100 hover:text-sage-700 disabled:opacity-50"
                        aria-label={t('admin_restore')} title={t('admin_restore')}>
                        <ArrowCounterClockwise size={16} />
                      </button>
                      <button onClick={() => setHardConfirm(b)} disabled={busy}
                        data-testid={`admin-building-hard-delete-${b.id}`}
                        className="p-2 rounded-lg text-ink-soft hover:bg-terracotta-100 hover:text-terracotta-700 disabled:opacity-50"
                        aria-label={t('admin_hard_delete')} title={t('admin_hard_delete')}>
                        <Warning size={16} weight="bold" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => setEditing(b)} data-testid={`admin-building-edit-${b.id}`}
                        className="p-2 rounded-lg text-ink-soft hover:bg-bg-alt hover:text-ink" aria-label={t('edit')}>
                        <PencilSimple size={16} />
                      </button>
                      <button onClick={() => setConfirm(b)} data-testid={`admin-building-delete-${b.id}`}
                        className="p-2 rounded-lg text-ink-soft hover:bg-terracotta-100 hover:text-terracotta-700" aria-label={t('admin_delete')}>
                        <Trash size={16} />
                      </button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing && (
        <BuildingForm
          building={editing === 'new' ? null : editing}
          orgs={orgs}
          showOrgPicker={isSuperAdmin}
          activeOrg={current}
          onCancel={() => setEditing(null)}
          onSave={onSave}
          busy={busy}
        />
      )}

      <ConfirmDialog
        open={!!confirm}
        title={t('admin_building_delete_title')}
        description={confirm ? t('admin_building_delete_confirm', { name: confirm.name }) : ''}
        confirmLabel={t('admin_delete')}
        danger busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={onDelete}
      />

      <ConfirmDialog
        open={!!hardConfirm}
        title={t('admin_hard_delete_title')}
        description={hardConfirm ? t('admin_hard_delete_confirm', { name: hardConfirm.name }) : ''}
        confirmLabel={t('admin_hard_delete')}
        danger busy={busy}
        onCancel={() => setHardConfirm(null)}
        onConfirm={onHardDelete}
      />
    </AdminPage>
  );
}

function BuildingForm({ building, orgs, showOrgPicker, activeOrg, onCancel, onSave, busy }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    name: building?.name || '',
    description: building?.description || '',
    organization_id: building?.organization_id || activeOrg?.id || '',
  });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    const body = { name: form.name.trim(), description: form.description || null };
    if (showOrgPicker && form.organization_id) body.organization_id = form.organization_id;
    onSave(body);
  };

  return (
    <Modal onCancel={onCancel}>
      <form onSubmit={submit} data-testid="admin-building-form" className="space-y-4">
        <h2 className="font-heading text-2xl text-ink">
          {building ? t('admin_building_edit_title') : t('admin_building_new_title')}
        </h2>

        <Field label={t('admin_field_name')} required>
          <input type="text" required value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="admin-building-name" className={inputCls} />
        </Field>
        <Field label={t('admin_field_description')}>
          <textarea rows={2} value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="admin-building-description" className={inputCls} />
        </Field>
        {showOrgPicker && orgs.length > 0 && (
          <Field label={t('admin_field_organization')}>
            <select value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
              data-testid="admin-building-org" className={selectCls}>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </Field>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-stone_line">
          <button type="button" onClick={onCancel} disabled={busy} data-testid="admin-building-cancel"
            className="px-4 py-2 rounded-lg text-sm text-ink-soft hover:bg-bg-alt disabled:opacity-50">
            {t('cancel')}
          </button>
          <button type="submit" disabled={busy || !form.name.trim()} data-testid="admin-building-save"
            className="px-4 py-2 rounded-lg bg-sage text-white text-sm font-medium hover:bg-sage-700 disabled:opacity-50">
            {busy ? '…' : t('save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
